import type { ErrorEvent } from '@sentry/nextjs';

import { EVENT_RATE_LIMIT } from '@/shared/constants/sentry-constants';

/**
 * Client-side event cap for Sentry.
 *
 * The occurrence-threshold batcher this replaced suppressed the FIRST N occurrences of
 * an error, which hid real one-off failures. This does the inverse: the first occurrence
 * of any issue always sends, and only repeats past a per-window cap are dropped. That
 * bounds a flood without ever hiding something novel.
 *
 * State is per-tab and in-memory, so it resets on reload — deliberately, since a fresh
 * page load is a fresh chance for an error to be worth seeing.
 */
const counts = new Map<string, { count: number; windowStart: number }>();
let total = { count: 0, windowStart: Date.now() };

/** Group by fingerprint when we set one, else by exception type+value. */
function getEventKey(event: ErrorEvent): string {
	if (event.fingerprint?.length) return event.fingerprint.join('|');
	const exception = event.exception?.values?.[0];
	return `${exception?.type ?? 'unknown'}|${exception?.value ?? event.message ?? 'unknown'}`;
}

function withinWindow(windowStart: number, now: number): boolean {
	return now - windowStart < EVENT_RATE_LIMIT.WINDOW_MS;
}

/**
 * Returns true when the event should be sent, false when it should be dropped.
 * Dropped events are counted so the resumed event can report how many were suppressed.
 */
export function shouldSendEvent(event: ErrorEvent): boolean {
	try {
		const now = Date.now();

		if (!withinWindow(total.windowStart, now)) total = { count: 0, windowStart: now };
		total.count += 1;
		if (total.count > EVENT_RATE_LIMIT.MAX_TOTAL) return false;

		const key = getEventKey(event);
		const entry = counts.get(key);

		if (!entry || !withinWindow(entry.windowStart, now)) {
			counts.set(key, { count: 1, windowStart: now });
			return true;
		}

		entry.count += 1;
		if (entry.count > EVENT_RATE_LIMIT.MAX_PER_KEY) return false;

		// Mark the last event before the cap so the issue says it was throttled.
		if (entry.count === EVENT_RATE_LIMIT.MAX_PER_KEY) {
			event.tags = { ...event.tags, rate_limited: 'true' };
		}
		return true;
	} catch {
		// Never let the limiter be the reason an error goes unreported.
		return true;
	}
}
